import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { useCredits } from '@/hooks/useCreditBalance';
import { BuyCreditModal } from '@/components/credits/BuyCreditModal';
import { PageSkeleton } from '@/components/ui/PageSkeleton';
import { logger } from '@/utils/productionLogger';

interface RequireCreditsProps {
  children: React.ReactNode;
  minCredits?: number;
}

/**
 * Composant RequireCredits - Protection de route pour les actions nécessitant des crédits
 * 
 * Ce composant protège les routes qui nécessitent des crédits disponibles.
 * Si l'utilisateur n'a pas assez de crédits, il affiche une modale d'achat
 * et redirige vers le dashboard après fermeture.
 * 
 * Utilisation :
 * ```tsx
 * <Route
 *   path="/create-campaign"
 *   element={
 *     <RequireCredits>
 *       <CreateCampaign />
 *     </RequireCredits>
 *   }
 * />
 * ```
 * 
 * @param children - Composant enfant à protéger
 * @param minCredits - Nombre minimum de crédits requis (défaut: 1)
 */
export const RequireCredits = ({ children, minCredits = 1 }: RequireCreditsProps) => {
  const navigate = useNavigate();
  const { profile } = useAuth();
  const organizationId = profile?.organization_id || undefined;
  const { hasCredits, loading: creditsLoading, refreshBalance } = useCredits(organizationId);
  const [showModal, setShowModal] = useState(false);
  const [hasChecked, setHasChecked] = useState(false);

  useEffect(() => {
    // Note: L'authentification est déjà vérifiée par RequireAuth
    // On vérifie uniquement organization_id et crédits ici
    
    // Attendre que les crédits soient chargés
    if (creditsLoading) return;

    // Vérifier que l'utilisateur a une organisation
    if (!organizationId) {
      logger.error('RequireCredits: User has no organization_id', new Error('Missing organization_id'), {
        userId: profile?.id,
        email: profile?.email
      });
      
      navigate('/dashboard', {
        state: {
          error: 'Configuration incomplète',
          message: 'Votre profil doit être rattaché à une organisation'
        }
      });
      return;
    }

    // Vérifier les crédits
    if (!hasCredits(minCredits)) {
      logger.warn('RequireCredits: Insufficient credits', {
        organizationId,
        minCredits,
        hasChecked
      });
      
      setShowModal(true);
      setHasChecked(true);
    } else {
      logger.debug('RequireCredits: Credits check passed', {
        organizationId,
        minCredits
      });
      
      setHasChecked(true);
    }
  }, [creditsLoading, hasCredits, minCredits, organizationId, profile, navigate, hasChecked]);

  // Gérer la fermeture de la modale
  const handleModalClose = async (open: boolean) => {
    setShowModal(open);
    
    // Si on ferme la modale sans acheter, retourner au dashboard
    if (!open && !hasCredits(minCredits)) {
      logger.debug('RequireCredits: Modal closed without purchase, redirecting to dashboard');
      navigate('/dashboard');
    } else if (!open && hasCredits(minCredits)) {
      // Si l'utilisateur a acheté des crédits, rafraîchir et réessayer la réservation
      await refreshBalance();
      setHasChecked(false); // Forcer une nouvelle vérification
    }
  };

  // Note: L'authentification est déjà vérifiée par RequireAuth
  // Afficher un skeleton pendant la vérification des crédits
  if (creditsLoading || !hasChecked) {
    return <PageSkeleton />;
  }

  // Si pas de crédits, afficher la modale
  if (!hasCredits(minCredits)) {
    return (
      <>
        <div className="flex items-center justify-center min-h-screen">
          <div className="text-center">
            <p className="text-gray-600">Crédits insuffisants</p>
          </div>
        </div>
        <BuyCreditModal 
          open={showModal} 
          onOpenChange={handleModalClose}
        />
      </>
    );
  }

  // Crédits suffisants, afficher le contenu avec animation fade-in
  return (
    <div className="animate-in fade-in duration-300 w-full h-full">
      {children}
    </div>
  );
};
